import type {IModifyTemplateGraph} from "@cm/template-nodes"
import type {
    InitStackableMattressStandardModelMasterTemplateOptions,
    InitMattressInstanceTemplateOptions,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {
    initInstanceTemplateCommon,
    initStackableMattressStandardModelMasterTemplate,
    initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS006A,
    initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS012A,
    initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS007A,
    getDefaultHandlesParameters,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import {createTemplateReference} from "#mattress/assets-definitions/common"
import {createMattress} from "#mattress/assets-definitions/create-mattress"
import type {MattressUVControlPoint} from "#mattress/nodes/curve/mattress-curve"

const waterfallDefaults: InitStackableMattressStandardModelMasterTemplateOptions = {
    openingOnBottom: false,
    topPartFabricLayout: "Waterfall",
    bottomPartFabricLayout: "Border",
    middlePartFabricLayout: "Border",
}

const waterfallWithMirrorDefaults: InitStackableMattressStandardModelMasterTemplateOptions = {
    ...waterfallDefaults,
    bottomPartFabricLayout: "BorderWithMirror",
}

export function initSimpleWaterfallMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    initStackableMattressStandardModelMasterTemplate(modifyTemplateGraph, {...waterfallDefaults, ...options, bottomPanelMode: "edge"})
}

export function initSimpleWaterfallInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 8,
        "bottom-height": 16,
        "corner-radius": 2,
        "top-radius": 2,
        "bottom-radius": 2,
        "layer-count": 0,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["piping 5.5mm"]),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Simple Waterfall (Master)",
            instanceId: "simple-waterfall-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initWaterfallWithMirrorMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: true},
) {
    initStackableMattressStandardModelMasterTemplate(modifyTemplateGraph, {...waterfallWithMirrorDefaults, ...options, bottomPanelMode: "mirror"})
}

export function initWaterfallWithMirrorInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 8,
        "bottom-height": 16,
        "corner-radius": 2,
        "top-radius": 2,
        "bottom-radius": 2,
        "layer-count": 0,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["piping 5.5mm"]),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Waterfall with mirror (Master)",
            instanceId: "waterfall-with-mirror-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initWaterfallWithExtraLaneMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    initStackableMattressStandardModelMasterTemplate(modifyTemplateGraph, {...waterfallDefaults, ...options, bottomPanelMode: "edge"})
}

export function initWaterfallWithExtraLaneInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 8,
        "bottom-height": 8,
        "corner-radius": 2,
        "top-radius": 2,
        "bottom-radius": 2,
        "layer-count": 1,
        "fabric-stack/layer-0/height": 6,
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-count": 1,
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-stack/accessory-0/accessory": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["piping 2mm"],
        ),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Waterfall with extra lane (Master)",
            instanceId: "waterfall-with-extra-lane-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

export function initWaterfallWithMirrorAndExtraLaneMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: true},
) {
    initStackableMattressStandardModelMasterTemplate(modifyTemplateGraph, {...waterfallWithMirrorDefaults, ...options, bottomPanelMode: "mirror"})
}

export function initWaterfallWithMirrorAndExtraLaneInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 8,
        "bottom-height": 8,
        "corner-radius": 2,
        "top-radius": 2,
        "bottom-radius": 2,
        "layer-count": 1,
        "fabric-stack/layer-0/height": 6,
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-count": 1,
        "fabric-stack/layer-0/accessories-layer-top-curve/accessory-stack/accessory-0/accessory": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["piping 5.5mm"],
        ),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Waterfall with mirror and extra lane (Master)",
            instanceId: "waterfall-with-mirror-and-extra-lane-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

const controlPointsData_BS006A: Omit<MattressUVControlPoint, "uvSpace">[] = [
    {
        uv: [0, -1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.1, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.185, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.2, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.25, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.3, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.7, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.75, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.8, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.815, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.9, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [1.0, -1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.9, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.815, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.8, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.75, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.7, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.3, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.25, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.2, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.185, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.1, 0],
        sectionBreak: false,
        corner: false,
    },
]

export function initWaterfallBS006AMasterTemplate(modifyTemplateGraph: IModifyTemplateGraph, options: InitStackableMattressStandardModelMasterTemplateOptions) {
    const rootNodes = initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS006A(
        {...waterfallWithMirrorDefaults, ...options},
        {
            patternVerticalSize: 8,
            controlPointsData: controlPointsData_BS006A,
        },
    )
    const mattress = createMattress([
        rootNodes.mattressMesh,
        rootNodes.refCurveGroup,
        rootNodes.dimensionsGroup,
        rootNodes.topPartGroup,
        rootNodes.middlePartGroup,
        rootNodes.bottomPartGroup,
        // rootNodes.handlesGroup,
    ])
    modifyTemplateGraph.modifyTemplateGraph((graph) => {
        graph.parameters.nodes.updateParameters({list: [mattress]})
    })
}

export function initWaterfallBS006AInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 8,
        "bottom-height": 16,
        "corner-radius": 3,
        "top-radius": 3,
        "bottom-radius": 3,
        "layer-count": 0,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["piping 5.5mm"]),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Waterfall BS006A (Master)",
            instanceId: "waterfall-BS006A-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

const controlPointsData_BS012A: Omit<MattressUVControlPoint, "uvSpace">[] = [
    {
        uv: [0, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.1, -1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.2, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.25, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.3, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.7, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.75, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [0.8, 0],
        sectionBreak: false,
        corner: false,
    },

    {
        uv: [0.9, -1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [1.0, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.9, 1],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.8, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.75, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.7, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.3, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.25, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.2, 0],
        sectionBreak: false,
        corner: false,
    },
    {
        uv: [-0.1, 1],
        sectionBreak: false,
        corner: false,
    },
]

export function initWaterfallBS012AMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    const rootNodes = initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS012A(
        {...waterfallWithMirrorDefaults, ...options},
        {
            patternVerticalSize: 8,
            controlPointsData: controlPointsData_BS012A,
        },
    )
    const mattress = createMattress([
        rootNodes.mattressMesh,
        rootNodes.refCurveGroup,
        rootNodes.dimensionsGroup,
        rootNodes.topPartGroup,
        rootNodes.middlePartGroup,
        rootNodes.bottomPartGroup,
        // rootNodes.handlesGroup,
    ])
    modifyTemplateGraph.modifyTemplateGraph((graph) => {
        graph.parameters.nodes.updateParameters({list: [mattress]})
    })
}

export function initWaterfallBS012AInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 12,
        "bottom-height": 12.01,
        "corner-radius": 3,
        "top-radius": 3,
        "bottom-radius": 3,
        "layer-count": 0,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["piping 5.5mm"]),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Waterfall BS012A (Master)",
            instanceId: "waterfall-BS012A-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}

const controlPointsData_BS007A: Omit<MattressUVControlPoint, "uvSpace">[] = [
    {
        uv: [0.2, -1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [0.25, -1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [0.3, -1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [0.7, -1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [0.75, -1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [0.8, -1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.96, -1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.92, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.8, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.75, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.7, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.3, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.25, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.2, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.08, 1],
        sectionBreak: false,
        corner: true,
    },
    {
        uv: [-0.04, -1],
        sectionBreak: false,
        corner: true,
    },
]

export function initWaterfallBS007AMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    const rootNodes = initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS007A(
        {...waterfallWithMirrorDefaults, ...options},
        {
            patternVerticalSize: 12,
            controlPointsData: controlPointsData_BS007A,
        },
    )
    const mattress = createMattress([
        rootNodes.mattressMesh,
        rootNodes.refCurveGroup,
        rootNodes.dimensionsGroup,
        rootNodes.topPartGroup,
        rootNodes.middlePartGroup,
        rootNodes.bottomPartGroup,
        // rootNodes.handlesGroup,
    ])
    modifyTemplateGraph.modifyTemplateGraph((graph) => {
        graph.parameters.nodes.updateParameters({list: [mattress]})
    })
}

export function initWaterfallBS007AInstanceTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateDataLegacyId: number,
    options: InitMattressInstanceTemplateOptions,
) {
    const optionsParameters = {
        ...(options.parameters ? options.parameters : {}),
        width: 90,
        length: 200,
        "top-height": 6,
        "bottom-height": 18,
        "corner-radius": 3,
        "top-radius": 3,
        "bottom-radius": 3,
        "layer-count": 0,
        "accessories-bottom-curve/accessory-count": 1,
        "accessories-bottom-curve/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["piping 5.5mm"]),
        "accessories-bottom-panel/accessory-count": 1,
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory": createTemplateReference(options.accessoriesTemplatesLegacyIds["zipper Teeth, M5"]),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-template": createTemplateReference(
            options.accessoriesTemplatesLegacyIds["puller Standard Puller (YKK, n56) with Slider, M5"],
        ),
        "accessories-bottom-panel/accessory-stack/accessory-0/accessory/puller-0/puller-offset": 0.995,
        ...getDefaultHandlesParameters(options),
    }

    initInstanceTemplateCommon(
        modifyTemplateGraph,
        {
            legacyId: masterTemplateDataLegacyId,
            instanceName: "Waterfall BS007A (Master)",
            instanceId: "waterfall-BS007A-master",
        },
        {
            ...options,
            parameters: optionsParameters,
        },
    )
}
